import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Heart, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  usePageTitle('My Dashboard');
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsLoading(false);
      fetchFavorites();
      return;
    }

    // Otherwise check auth status
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      setUser(response.data);
      fetchFavorites();
    } catch (error) {
      toast.error('Please sign in to access dashboard');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('token');
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-16">
        <div>
          <h1
            className="text-5xl md:text-6xl font-medium tracking-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="dashboard-title"
          >
            Welcome, <span className="text-gold">{user?.name}</span>
          </h1>
          <p className="text-lg text-white/60">Manage your saved yachts and preferences</p>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="text-white/60 hover:text-white"
          data-testid="dashboard-logout-btn"
        >
          <LogOut size={18} className="mr-2" />
          Logout
        </Button>
      </div>

      {/* User Info */}
      <div className="glass-light p-8 mb-12">
        <h2 className="text-2xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Account Information
        </h2>
        <div className="space-y-2 text-white/70">
          <p>
            <span className="text-white font-semibold">Email:</span> {user?.email}
          </p>
          <p>
            <span className="text-white font-semibold">Member Since:</span>{' '}
            {new Date(user?.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* Saved Yachts */}
      <div>
        <h2
          className="text-3xl font-medium mb-8"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="favorites-title"
        >
          Saved Yachts <Heart className="inline text-gold" size={28} />
        </h2>

        {favorites.length === 0 ? (
          <div className="glass-light p-12 text-center" data-testid="no-favorites">
            <Heart className="mx-auto mb-4 text-white/20" size={48} />
            <p className="text-white/60 mb-6">You haven't saved any yachts yet</p>
            <Link to="/listings">
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">Browse Yachts</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="favorites-grid">
            {favorites.map((yacht) => (
              <Link
                key={yacht.yacht_id}
                to={`/yacht/${yacht.yacht_id}`}
                className="group relative overflow-hidden glass-light hover:border-gold/30 transition-all duration-500 h-[500px]"
                data-testid={`favorite-yacht-${yacht.yacht_id}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{ backgroundImage: `url('${yacht.images[0]}')` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent"></div>
                </div>
                <div className="relative h-full flex flex-col justify-end p-8">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{yacht.builder}</p>
                  <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {yacht.name}
                  </h3>
                  <p className="text-white/60 text-sm mb-4">{yacht.location}</p>
                  <p className="text-gold font-semibold">
                    {yacht.charter_weekly_usd
                      ? `$${yacht.charter_weekly_usd.toLocaleString()} / week`
                      : `$${yacht.price_usd?.toLocaleString()}`}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
